import { css } from '@emotion/core'
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Card,
  Row,
  Button,
  Expander,
  ExpanderHeader,
  ExpanderContent,
  Divider,
  BillboardButton,
  useOverlay,
  InfoSheetPanel,
  H3,
  Paragraph,
  Separator,
} from '@fs/zion-ui'
import ZionForm, { useZionForm, FormSelect, FormRadioGroup, FormRadio } from '@fs/zion-form'
import { ContentAdd, ContentDelete } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import zionDebug from '@fs/zion-debug'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import {
  generateSls,
  getImage,
  getMostRecentFieldValue,
  getNameType,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getPersonName,
  getSexEnumFromResource,
  getSexTranslatedOrOrig,
  sortPersonNameSubElements,
  useGenderTranslations,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import FormStandardizedSexField from '../../../../components/standardizedSexField/StandardizedSexField'
import Tip from '../../../../components/tip/Tip'
import EditPersonFormElement from './EditPersonFormElement'
import { getNextUuid } from '../../../../../shared/utils/utils'
import PanelHeader from './PanelHeader'
import FieldSelectorOverlay from '../../../../components/generic/panels/shared/fieldChipSelector/FieldSelectorOverlay'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import {
  submitEditFields,
  handleOnFocus,
  handleFieldChange,
  removeFieldFromValues,
  getCreateDispatchesForSingleField,
} from './utils/editPanelFieldHelpers'
import SaveComponent from '../../../../components/generic/panels/shared/SaveComponent'
import RowWrapper from '../ui/RowWrapper'
import FieldWrapper from '../ui/FieldWrapper'
import useTreatedNameFieldChips, { disableSaveFuncRequireOneField } from '../../hooks/useTreatedNameFieldChips'
import useCommonTranslations from '../../hooks/useCommonTranslations'
import HighlightButtonOld from '../../../../components/generic/panels/shared/HighlightButtonOld'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { R2AppName } from '../../../../../shared/services/config'

const debug = zionDebug('rip:R2:EditEssentialInfo')

const fieldButtonsCss = css`
  margin-top: -10px;
  margin-inline-end: 0;
  margin-bottom: 10px;
  margin-inline-start: 10px;
  display: flex;
  flex-flow: column;
`
const removeMarginCss = css`
  & div {
    margin-bottom: 0px;
  }
`

// The intermediary element is the same as the child and there is only 1 child so only display one
const parentSameAsField = (parent, field) =>
  (parent?.subElements?.length === 1 && parent?.elementType === field?.fieldType) ||
  parent?.fieldType?.startsWith('NAME_')

function NameElement({ element, methods, setNameElements, nameElements }) {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const dispatch = useR2DispatchContext()
  const nameFieldSelectorOverlay = useOverlay()
  const [selectedFields, setSelectedFields] = useState([])
  const [selectedTempChips, setSelectedTempChips] = useState([])
  const [lockedTempChips, setLockedTempChips] = useState([])
  const { restrictedFields } = useCollectionContextProvider()
  const isRestricted = restrictedFields?.includes('ADD_REMOVE_FIELDS')
  const { selectedFieldData, treatedNameFieldChipData } = useTreatedNameFieldChips(
    element,
    state.types,
    selectedFields,
    selectedTempChips,
    lockedTempChips
  )
  const addMoreFields = `${t('edit-index.add.button', 'Add or Remove Fields')} ${t(
    'edit-index.chips.name-fields.title',
    'Name'
  )}`
  const { ADD_MORE_INFO } = useCommonTranslations()
  useEffect(() => {
    setSelectedFields(cloneDeep(selectedFieldData).filter((field) => field?.typeData?.editable))
  }, [selectedFieldData])

  const handleNameFieldsChipSelection = (chips, lockedChips) => {
    const outFieldsThatStayed = (field) =>
      !chips.map(({ id }) => id).includes(field.id) && !lockedChips.map(({ id }) => id).includes(field.id)
    const newFields = [...selectedFields]
    const removeField = (field) => {
      removeFieldFromValues(field, methods)
      const index = newFields.findIndex((f) => f.id === field.id)
      newFields.splice(index, 1)
    }
    selectedFields.filter(outFieldsThatStayed).forEach(removeField)

    const newChips = chips.filter((chip) => !chip.id)
    if (newChips) {
      newChips.forEach((chip) => {
        const newField = {
          label: chip.label,
          fieldType: chip.value.split(':')[0],
          id: getNextUuid(),
          added: true,
        }
        newFields.push(newField)
        methods.setValue(newField.id, '')
        methods.setValue(`${newField.id}_nameField`, chip.value.split(':')[0])
        methods.setValue(`${newField.id}_nameSuper`, element.id)
      })
    }
    // create an object in the shape of what sortPersonNameSubElements can sort
    const pseudoPerson = {
      subElements: [{ elementType: 'NAME', subElements: newFields }],
    }
    sortPersonNameSubElements(pseudoPerson)
    setSelectedFields(newFields)
  }

  return (
    <RowWrapper>
      {selectedFields.map((field) => {
        const { fieldType } = field
        return (
          <FieldWrapper key={field.id} indent="0">
            {fieldType !== 'NAME_SURN' &&
              (field?.added || parentSameAsField(field, field?.subElements?.[0]) || !field.subElements) && (
                <EditPersonFormElement field={field} />
              )}
            {fieldType === 'NAME_SURN' && (
              <EditPersonFormElement
                {...{
                  field,
                  handleOnFocus: () => handleOnFocus({ ...r2State, ...state, r2dispatch: dispatch, element: field }),
                  handleFieldChange: () => handleFieldChange(dispatch),
                }}
              />
            )}
          </FieldWrapper>
        )
      })}
      <BillboardButton
        data-testid="add-or-remove-fields-button"
        color="yellow"
        emphasis="low"
        disabled={isRestricted}
        Icon={ContentAdd}
        onClick={nameFieldSelectorOverlay.handleClick}
        alignY="nextToInput"
      >
        {ADD_MORE_INFO}
      </BillboardButton>
      {nameElements?.length > 1 && (
        <BillboardButton
          data-testid="remove-name-button"
          color="red"
          emphasis="low"
          Icon={ContentDelete}
          onClick={() => {
            if (!element.pseudoName) {
              methods.setValue(element.id, 'REMOVED')
            } else {
              methods.unregister(element.id)
              methods.unregister(`${element.id}_newNameElement`)

              // unregister all subElement form-values related to the name element being removed. (NAME_GN, etc.)
              // formValuesObject is an object with keys and values that represent the form keys and values
              const formValuesObject = methods.getValues()
              // Get all the object keys that have the name element id as a value. They need to be removed.
              // These represent backend calls to create subElement relationships with the name element being removed
              // Thus, we need to remove them and all of their related subElement form values
              const partialFormKeys = Object.entries(formValuesObject)
                .filter(([, value]) => value === element.id)
                // It's not as simple as removing these keys from the formValuesObject
                // The keys are a string composed of an id and a descriptor
                // parse out the id from the descriptor part of the key
                .map(([key]) => key.split('_')[0])
              // Now with the id parts of the keys, we can search for all the keys that start with that id
              const keysToRemove = Object.keys(formValuesObject).filter((key) =>
                partialFormKeys.some((partialKey) => key.startsWith(partialKey))
              )
              // Now we can unregister all of the keys that start with the id
              keysToRemove.forEach((key) => methods.unregister(key))
              // Wow, that was a lot... Glad to have comments though...
            }
            element.removed = true
            setNameElements(nameElements.filter((ne) => !ne.removed))
          }}
          alignY="nextToInput"
        >
          {t('edit-index.remove-name.button', 'Remove Name')}
        </BillboardButton>
      )}
      <FieldSelectorOverlay
        allowSelectingMultiple={false}
        title={addMoreFields}
        chipData={[
          {
            title: t('edit-index.chips.most-common-name.title', 'Most Common Name'),
            data: treatedNameFieldChipData.filter((chip) =>
              ['NAME_GN', 'NAME_SURN', 'NAME_PATR', 'NAME_MATR'].includes(chip.value)
            ),
            key: `commonNameFields-${element.id}`,
          },
          {
            title: t('edit-index.chips.prefix-suffix.title', 'Prefix and Suffix'),
            data: treatedNameFieldChipData.filter((chip) => ['NAME_PRE', 'NAME_SUF', 'NAME_SPRE'].includes(chip.value)),
            key: `prefixSuffixFields-${element.id}`,
          },
        ]}
        fieldData={selectedFields}
        handleSelection={handleNameFieldsChipSelection}
        handleSelectionChange={(chips, lockedChips) => {
          setSelectedTempChips(chips)
          setLockedTempChips(lockedChips)
        }}
        overlay={nameFieldSelectorOverlay}
        disabledFunc={disableSaveFuncRequireOneField}
      />
    </RowWrapper>
  )
}

export default function EditEssentialInfo() {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2Dispatch = useR2DispatchContext()
  const { setDisplayHighlights, clearEdits, getDispatches } = useSlsHighlightsContext()

  const currentImage = getImage(state?.images, state?.currentImageArk)
  const selectedPerson = getNestedOrFloatingElementById([currentImage], r2State.selectedPersonId)

  const [initialNameElements, setInitialNameElements] = useState([])

  const [pseudoNames, setPseudoNames] = useState(() => [])
  const [nameElements, setNameElements] = useState(() => [])
  const [deletingSexCode, setDeletingSexCode] = useState(false)
  const [deletingRace, setDeletingRace] = useState(false)
  const genderTranslations = useGenderTranslations()

  const { restrictedFields } = useCollectionContextProvider()
  const isRestrictedNameField = restrictedFields?.includes('NAME')
  const isRestrictedSexField = restrictedFields?.includes('SEX')

  const reinitNameElements = useCallback(() => {
    setPseudoNames([])
    const personNameElements = selectedPerson?.subElements?.filter((se) => se.elementType === 'NAME') || []
    setInitialNameElements(cloneDeep(personNameElements))
  }, [selectedPerson?.subElements])

  useEffect(() => {
    reinitNameElements()
  }, [reinitNameElements])

  // Filter and process fields for the ui
  useEffect(() => {
    setNameElements([...initialNameElements, ...pseudoNames].filter((ne) => !ne.removed))
  }, [pseudoNames, initialNameElements])

  const checkIsEditable = (field) => {
    return field?.typeData?.editable ? field : undefined
  }

  const [addedSexCode, setAddedSexCode] = useState({})
  const [addedRaceOrColor, setAddedRaceOrColor] = useState()
  const sexCodeElement = checkIsEditable(getNestedSubelementsByType(selectedPerson, 'SEX_CODE')?.[0])
  const raceOrColorElement = checkIsEditable(getNestedSubelementsByType(selectedPerson, 'RACE_OR_COLOR')?.[0])
  const sexCode = useMemo(() => {
    return sexCodeElement || addedSexCode
  }, [sexCodeElement, addedSexCode])
  const raceOrColor = useMemo(() => {
    return raceOrColorElement || addedRaceOrColor
  }, [raceOrColorElement, addedRaceOrColor])
  const { editEssentialInfoOverlay, highlightsPanelOverlay, closeOverlay, topMostPanel, inPanelStack } =
    useOverlayContext()
  const saveChangesOverlay = useOverlay()
  const nameTypeSelectOptions = useMemo(
    () =>
      state.types?.names?.map((name) => {
        return {
          value: name.nameType,
          label: name.displayLabel,
        }
      }),
    [state.types?.names]
  )

  const getDefaultValues = useCallback(() => {
    const formDefaultValues = {}
    const personNameElements = selectedPerson?.subElements?.filter((se) => se.elementType === 'NAME')
    if (personNameElements) {
      personNameElements.forEach((element) => {
        // populate the value of the name type FormSelect
        const nameType = getNameType(nameTypeSelectOptions, element)
        formDefaultValues[element.id] = nameType ? nameType?.value : ''
        if (element && element.subElements) {
          element.subElements.forEach((se) => {
            if (se && se.subElements) {
              se.subElements.forEach((field) => {
                formDefaultValues[field.id] = getMostRecentFieldValue(field) || '' // Set a string value instead of undefined so dirty checking will work properly.
              })
            }
          })
        }
      })
    }
    formDefaultValues.primaryRadio = selectedPerson?.primary ? 'yes' : 'no'

    if (raceOrColorElement) {
      formDefaultValues[raceOrColorElement.id] = getMostRecentFieldValue(raceOrColorElement) || ''
    }
    // This ensures that the save button is disabled if sex is not standard
    if (sexCodeElement?.id) {
      formDefaultValues[sexCodeElement?.id] = {
        primaryText: getSexTranslatedOrOrig(sexCodeElement, genderTranslations) || '',
        key: getSexEnumFromResource(sexCodeElement) || '',
      }
    }
    formDefaultValues[r2State.addedSexId] = { primaryText: '', key: '' }
    return formDefaultValues
  }, [
    genderTranslations,
    nameTypeSelectOptions,
    r2State.addedSexId,
    raceOrColorElement,
    selectedPerson?.primary,
    selectedPerson?.subElements,
    sexCodeElement,
  ])

  const formInitialValues = useMemo(getDefaultValues, [
    getDefaultValues,
    nameTypeSelectOptions,
    r2State.addedSexId,
    raceOrColorElement,
    selectedPerson?.primary,
    selectedPerson?.subElements,
    sexCodeElement,
    t,
  ])

  const methods = useZionForm({
    mode: 'all',
    resolver: (values) => {
      const errors = {}
      Object.entries(values).forEach(([key, value]) => {
        // make sure the user selects a name type before saving a new name
        if (Object.keys(values).includes(`${key}_newNameElement`) && !value) {
          errors[key] = { message: t('edit-essential-info.must-select-name-type.error', 'Must select a name type') }
        }
      })
      return { values, errors }
    },
    defaultValues: formInitialValues,
  })

  const {
    reset,
    setValue,
    formState: { defaultValues },
  } = methods

  useUnsavedChangesOverlay({
    debug,
    debugMessage: 'essential info subscriber called: ',
    methods,
    overlay: saveChangesOverlay,
  })

  /*
    When editing shared fields like the surname, a new field is added to the record. 
    The useZionForm hook, which manages form state, does not detect changes to the 
    field structure after the initial render. Therefore, we need to manually reset 
    the form when the keys in formInitialValues differ from those in defaultValues, 
    indicating a change in the form's structure.
  */
  useEffect(() => {
    if (!formInitialValues || !defaultValues) return
    const formInitialValuesKeys = Object.keys(formInitialValues)
    const defaultValuesKeys = Object.keys(defaultValues)

    if (!isEqual(formInitialValuesKeys, defaultValuesKeys)) {
      reset(formInitialValues)
    }
  }, [formInitialValues, defaultValues, reset])

  const resetAndClose = () => {
    r2Dispatch({ type: 'REVERT_HIGHLIGHTS', value: {} })
    clearEdits()
    closeOverlay('editEssentialInfo')
  }

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    if (methods.formState.isDirty) saveChangesOverlay.open()
    else {
      resetAndClose()
    }
  }

  const handleSubmit = (values) => {
    const dispatches = submitEditFields({
      ...state,
      ...r2State,
      values,
      inPanelStack,
      formState: methods.formState,
      currentImageArk: state.currentImageArk,
    }).concat(
      getDispatches({
        mainImageArk: state.currentImageArk,
        splitScreenImageArk: thumbnails?.[r2State.referenceFocusedIndex]?.ark,
      })
    )
    // Clear out the localizedNames value to trigger PersonOverview to fetch it again.
    dispatches.push({
      type: 'SET_PERSON_LOCALIZED_NAME',
      value: {
        personId: r2State.selectedPersonId,
        localizedName: '',
        partitionKey: state.currentImageArk,
      },
    })
    return { sls: generateSls(dispatches), dispatches }
  }

  /**
   * This useEffect prepares dispatches to create a field in SLS when:
   * 1) the highlight being selected for the field is in use by one or more other fields
   * 2) the editSharedHighlightPage2 panel comes into view
   * 3) the field does not yet exist in SLS
   * The dispatches are saved in R2 state and use in editSharedHighlightPage2.js when preparing and submitting dispatches
   */
  useEffect(() => {
    const existingField = getNestedOrFloatingElementById(state.images, r2State.highlightsFieldInfo.id)
    if (topMostPanel() === 'editSharedHighlightPage2' && r2State.editDispatches.length === 0 && !existingField) {
      const dispatchesForHighlightField = getCreateDispatchesForSingleField({
        ...state,
        ...r2State,
        inPanelStack,
        values: methods.getValues(),
        formState: methods.formState,
        currentImageArk: state.currentImageArk,
      })
      r2Dispatch({
        type: 'SET_EDIT_DISPATCHES',
        value: dispatchesForHighlightField,
      })
    }
  }, [inPanelStack, methods, r2Dispatch, r2State, state, topMostPanel])

  const finished = () => {
    reinitNameElements()
    closeOverlay('editEssentialInfo')
  }

  editEssentialInfoOverlay.onClose = () => {
    reinitNameElements()
    methods.reset()
  }

  // UI Information
  const heading = t('editEssentialInfo.heading', 'Essential Information')
  const instructions = t(
    'edit-essential-info.instructions',
    'Fix any fields that are incorrect including their highlights. Add or remove fields as necessary.'
  )

  const addMissingRaceOrColor = () => {
    const newRaceOrColorId = raceOrColor?.id || getNextUuid()
    const newRaceOrColor = {
      id: newRaceOrColorId,
      value: '',
      fieldType: 'RACE_OR_COLOR',
      elementType: 'FIELD',
      label: t('edit-essential-info.race-or-color', 'Race Or Color'),
    }
    setDeletingRace(false)
    setAddedRaceOrColor(newRaceOrColor)
    methods.setValue(newRaceOrColorId, '') // Ensure value is reset
    methods.setValue(`${newRaceOrColorId}_fieldType`, 'RACE_OR_COLOR')
  }

  const addMissingSex = () => {
    setDeletingSexCode(false)
    setAddedSexCode({
      id: r2State.addedSexId,
      value: { primaryText: '', key: '' },
      fieldType: 'SEX_CODE',
      elementType: 'FIELD',
      displayLabel: t('edit-essential-info.sex-code', 'Sex'),
    })
  }

  useEffect(() => {
    if (addedSexCode?.id) {
      setValue(addedSexCode.id, addedSexCode.value)
      setValue(`${addedSexCode.id}_fieldType`, 'SEX_CODE')
    }
  }, [addedSexCode, setValue, r2State.addedSexId])

  useEffect(() => {
    if (addedRaceOrColor) {
      setValue(addedRaceOrColor.id, '')
      setValue(`${addedRaceOrColor.id}_fieldType`, 'RACE_OR_COLOR')
    }
  }, [addedRaceOrColor, setValue])

  useEffect(() => {
    if (editEssentialInfoOverlay.isOpen && topMostPanel() === 'editEssentialInfo' && !highlightsPanelOverlay.isOpen) {
      const elementIds = [r2State.selectedPersonId]
      const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark
      const fieldTypes = ['NAME_SURN', 'NAME_SUF', 'NAME_GN', 'NAME_PRE', 'SEX_CODE', 'RACE_OR_COLOR']

      setDisplayHighlights({
        mainPartitionKey: state.currentImageArk,
        splitScreenPartitionKey: splitScreenImageArk,
        fieldTypes,
        elementIds,
      })
    }
  }, [
    setDisplayHighlights,
    editEssentialInfoOverlay.isOpen,
    highlightsPanelOverlay.isOpen,
    topMostPanel,
    state.currentImageArk,
    r2State.selectedPersonId,
    r2State.referenceFocusedIndex,
    r2State.splitScreen,
    thumbnails,
  ])

  return (
    <ZionForm onSubmit={handleSubmit} methods={methods} id="edit-age">
      <InfoSheetPanel
        {...{ ...editEssentialInfoOverlay, close: userClosedPanel }}
        headingColor="green"
        title={t('edit-index.edit-person.heading', 'Edit {personName}', {
          personName: getPersonName({ person: selectedPerson }),
        })}
        footer={
          <SaveComponent
            saveChangesOverlay={saveChangesOverlay}
            onSubmit={handleSubmit}
            finished={finished}
            canceled={resetAndClose}
            appName={R2AppName}
            personId={r2State.userId}
            formName="edit-essential-info"
            saveInitiatorFilename="EditEssentialInfo.js"
          />
        }
      >
        <PanelHeader heading={heading} caption={instructions} headingSize="H5" />
        <Separator />
        <Tip
          useOverline={false}
          primaryText={t('edit-index.person-details.principal-name-tip-title', 'Help with Essential Information')}
          listPrimaryText={
            <>
              <span>
                <b>
                  {t('edit-index.person-details.principal-name-tip-title-1', '1. What is in Essential Information?')}
                </b>
              </span>
              <Separator size="xxs" />
              <Paragraph size="sm">
                {t(
                  'edit-index.person-details.principal-name-tip-text-1',
                  'When reviewing a record, most patrons first look at Name, Race, and Sex.'
                )}
              </Paragraph>
              <Separator size="sm" />
              <span>
                <b>{t('edit-index.person-details.principal-name-tip-title-2', '2. What is a primary name?')}</b>
              </span>
              <Separator size="xxs" />
              <Paragraph size="sm">
                {t(
                  'edit-index.person-details.principal-name-tip-text-2',
                  'The Primary Name is who the record is about.'
                )}
              </Paragraph>
              <Separator size="xxs" />
              <Paragraph size="sm">
                {t(
                  'edit-index.person-details.principal-name-tip-text-2-1',
                  "Usually there's 1 per record, unless it is a record such as marriage or divorce, then there are 2."
                )}
              </Paragraph>
            </>
          }
        />
        <Separator />
        <div css={removeMarginCss}>
          <FormRadioGroup
            disabled={isRestrictedNameField}
            label={t('edit-index.person-details.primary-label', 'Primary Name')}
            orientation="horizontal"
          >
            <FormRadio
              data-testid="principal-name-yes"
              name="primaryRadio"
              label={t('edit-index.person-details.primary-yes', 'Yes')}
              value="yes"
            />
            <FormRadio
              data-testid="principal-name-no"
              name="primaryRadio"
              label={t('edit-index.person-details.primary-no')}
              value="no"
            />
          </FormRadioGroup>
        </div>
        <Separator />
        {/* Prevent rendering while editSharedField panels are open. This prevents old surname values from being added to the form values, causing RIP-1394 */}
        {(topMostPanel() === 'editEssentialInfo' || topMostPanel() === 'highlightsPanelOld') && (
          <>
            {nameElements?.map((ne) => {
              const nameType = getNameType(nameTypeSelectOptions, ne)
              return (
                <Card edges="border" margins="dense" key={ne.id}>
                  <Expander initiallyOpen>
                    <ExpanderHeader>
                      <H3 size="H5">{nameType ? nameType.label : t('edit-essential-info.title', 'Name')}</H3>
                    </ExpanderHeader>
                    <ExpanderContent>
                      <FieldWrapper indent="0">
                        <FormSelect
                          name={ne.id}
                          label={t('edit-events.fields.name-type', 'Name Type')}
                          disabled={!ne.pseudoName}
                          options={nameTypeSelectOptions}
                        />
                      </FieldWrapper>
                      <NameElement
                        key={ne.id}
                        element={ne}
                        handleFieldChange={handleFieldChange}
                        methods={methods}
                        setNameElements={setNameElements}
                        nameElements={nameElements}
                      />
                    </ExpanderContent>
                  </Expander>
                </Card>
              )
            })}
            <Divider marginY="xs" />
            <Button
              data-testid="add-name-button"
              color="blue"
              disabled={isRestrictedNameField}
              emphasis="low"
              Icon={ContentAdd}
              onClick={() => {
                const id = getNextUuid()
                methods.setValue(`${id}_newNameElement`, id)
                methods.setValue(id, 'BIRTH_NAME')
                setPseudoNames([
                  ...pseudoNames,
                  {
                    id,
                    subElements: [],
                    partitionKey: selectedPerson.partitionKey,
                    pseudoName: true,
                  },
                ])
              }}
            >
              {t('edit-essential-info.add-name.button', 'Add Name')}
            </Button>
            <Divider marginY="xs" />
            {raceOrColor && !deletingRace && (
              <FieldWrapper indent="0">
                <EditPersonFormElement field={raceOrColor} />
                <div css={fieldButtonsCss}>
                  <BillboardButton
                    data-testid="delete-race"
                    Icon={ContentDelete}
                    disabled={isRestrictedNameField}
                    color="red"
                    emphasis="low"
                    iconSize="lg"
                    onClick={() => {
                      setDeletingRace(true)
                      methods.setValue(raceOrColor.id, 'REMOVED') // Set the value to "REMOVED"
                      methods.setValue(`${raceOrColor.id}_fieldType`, 'RACE_OR_COLOR')
                    }}
                  >
                    {t('edit-essential-info.delete-race.button', 'Delete Race')}
                  </BillboardButton>
                </div>
              </FieldWrapper>
            )}
            {(!raceOrColor || deletingRace) && (
              <Row>
                <Button data-testid="add-race-or-color" Icon={ContentAdd} onClick={addMissingRaceOrColor}>
                  {t('edit-essential-info.add-race', 'Add Race')}
                </Button>
              </Row>
            )}
            {sexCode && sexCode?.id && !deletingSexCode && (
              <>
                <Divider marginY="xs" />
                <FieldWrapper indent="0">
                  <FormStandardizedSexField
                    fieldName={getNestedSubelementsByType(selectedPerson, 'SEX_CODE')?.[0]?.id || addedSexCode?.id}
                    person={selectedPerson}
                    handleOnFocus={() => handleOnFocus({ r2Dispatch, element: sexCode })}
                    handleOnBlur={() => handleFieldChange(r2Dispatch)}
                    key={sexCode.id}
                    addedSexCode={addedSexCode}
                  />
                  <HighlightButtonOld field={sexCode} emphasis="low" />
                  <div css={fieldButtonsCss}>
                    <BillboardButton
                      data-testid="delete-sex"
                      Icon={ContentDelete}
                      color="red"
                      emphasis="low"
                      disabled={isRestrictedSexField}
                      iconSize="lg"
                      onClick={() => {
                        setDeletingSexCode(true)
                        setAddedSexCode({})
                        methods.setValue(sexCode.id, { primaryText: 'REMOVED', key: 'REMOVED' })
                        methods.trigger()
                      }}
                    >
                      {t('edit-essential-info.delete-sex.button', 'Delete Sex')}
                    </BillboardButton>
                  </div>
                </FieldWrapper>
              </>
            )}
            {(deletingSexCode || !sexCode?.id) && (
              <>
                <Divider marginY="xs" />
                <Row>
                  <Button data-testid="add-sex" Icon={ContentAdd} onClick={addMissingSex}>
                    {t('edit-essential-info.add-sex', 'Add Sex')}
                  </Button>
                </Row>
              </>
            )}
          </>
        )}
      </InfoSheetPanel>
    </ZionForm>
  )
}
